/**
 * @poc-to-production
 *
 * Floating prompt on the sheet viewer when the selected violation is
 * indeterminate — asks the architect for missing facts, persists versioned
 * audit entries, shows a clear verdict, then dismisses on OK.
 */

import type { UnitRef } from "@vera/rules";
import { evaluateRule, type QuantityValue, type RuleResult } from "@vera/rules";
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  type BulkApplyCandidate,
  elementFamilyLabel,
  findBulkApplyCandidates,
} from "../lib/bulkApplyFacts";
import {
  draftOverridesFromForm,
  mergedFactsForPreview,
  previewBannerForResult,
  previewRuleResult,
  previewViolationFromResult,
} from "../lib/factPreview";
import {
  editFieldsForUserSupplied,
  type MissingFactField,
  missingFactsForEvaluation,
  missingFactsForViolation,
  promptFieldsForViolation,
} from "../lib/missingFacts";
import type { UserFactRevision } from "../lib/userFactAuditDb";
import { latestUserFactsForElementRule } from "../lib/userFactAuditDb";
import { buildFactFormDraft, userFactValuesEqual } from "../lib/userFactDraft";
import { overridesForRule } from "../lib/userFactScope";
import { useEngineStore } from "../stores/engineStore";
import { useFactPreviewStore } from "../stores/factPreviewStore";
import { useCurrentProject } from "../stores/projectStore";
import {
  mergeElementWithUserFacts,
  type UserFactValue,
  useUserFactStore,
} from "../stores/userFactStore";
import {
  getSelectedViolation,
  useViolationsStore,
} from "../stores/violationsStore";

const UNIT_HINT: Record<UnitRef, string> = {
  millimetre: "mm",
  metre: "m",
  inch: "in",
  foot: "ft",
  squareFoot: "ft²",
  squareMetre: "m²",
  degree: "°",
  radian: "rad",
  unitless: "%",
};

interface ApplyResult {
  violationId: string;
  sourceDbId: number;
  status: string;
  summary: string;
  rationale?: string;
  applied: UserFactRevision[];
  observed?: RuleResult["observedValue"];
  threshold?: RuleResult["thresholdApplied"];
}

function verdictDisplay(
  status: string,
  rationale?: string
): {
  title: string;
  color: string;
  detail: string;
} {
  switch (status) {
    case "confirmed_violation":
      return {
        title: "Does not comply",
        color: "#e87070",
        detail:
          "This finding moves to Verified by you in the violations list (coral). Values are saved with version history.",
      };
    case "indeterminate":
      return {
        title: "FC2 still cannot decide",
        color: "#e5b85f",
        detail:
          rationale ??
          "All fields FC2 can ask for are filled, but something else blocks a final answer (for example a unit mismatch). See the inspector for details.",
      };
    case "compliant":
    case "compliant_via_exception":
      return {
        title: "Complies",
        color: "#22cc66",
        detail:
          "This rule passes with your supplied values. The row leaves the violations list.",
      };
    default:
      return {
        title: "No finding",
        color: "var(--fc2-text-dim)",
        detail: "The element passes this rule with the values you entered.",
      };
  }
}

function parseDraftValue(
  field: MissingFactField,
  raw: string
): UserFactValue | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (field.kind === "boolean") {
    if (trimmed === "true" || trimmed === "yes") return true;
    if (trimmed === "false" || trimmed === "no") return false;
    return undefined;
  }
  if (field.kind === "enum") return trimmed;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return undefined;
  return { value: num, unit: field.unit } satisfies QuantityValue;
}

export function MissingFactPrompt() {
  const project = useCurrentProject();
  const selected = useViolationsStore(getSelectedViolation);
  const rules = useEngineStore(s => s.rules);
  const elements = useEngineStore(s => s.elements);
  const overrides = useUserFactStore(s => s.overrides);
  const setFact = useUserFactStore(s => s.setFact);
  const setPreview = useFactPreviewStore(s => s.setPreview);
  const clearPreview = useFactPreviewStore(s => s.clear);

  const rule = useMemo(
    () =>
      selected?.provenance?.ruleId
        ? rules.find(r => r.ruleId === selected.provenance?.ruleId)
        : undefined,
    [selected, rules]
  );

  const element = useMemo(() => {
    const dbId = selected?.elementDbId;
    if (typeof dbId !== "number") return undefined;
    return elements.find(e => e.dbId === dbId);
  }, [selected, elements]);

  const ruleScopedOverrides = useMemo(() => {
    const dbId = selected?.elementDbId;
    if (typeof dbId !== "number" || !rule) return undefined;
    const revisions = latestUserFactsForElementRule(
      project.id,
      dbId,
      rule.ruleId
    );
    return overridesForRule(dbId, rule.ruleId, overrides, revisions);
  }, [selected, rule, overrides, project.id]);

  const isEditMode = useMemo(() => {
    if (!selected) return false;
    const hasUserFacts =
      (selected.provenance?.userSupplied?.facts.length ?? 0) > 0;
    return (
      selected.provenance?.status === "confirmed_violation" && hasUserFacts
    );
  }, [selected]);

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [justSaved, setJustSaved] = useState<UserFactRevision[]>([]);
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());
  const [bulkFactKeys, setBulkFactKeys] = useState<Set<string>>(new Set());
  const [bulkDone, setBulkDone] = useState<number | null>(null);
  const [showAllExceptionPaths, setShowAllExceptionPaths] = useState(false);
  const [recheckNote, setRecheckNote] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  const missingFactsOptions = useMemo(
    () => ({ allExceptionPaths: showAllExceptionPaths }),
    [showAllExceptionPaths]
  );

  const missing = useMemo(
    () =>
      selected
        ? missingFactsForViolation(
            selected,
            rule,
            element,
            ruleScopedOverrides,
            missingFactsOptions
          )
        : [],
    [selected, rule, element, ruleScopedOverrides, missingFactsOptions]
  );

  const promptFields = useMemo(
    () =>
      selected && !isEditMode
        ? promptFieldsForViolation(
            selected,
            rule,
            element,
            ruleScopedOverrides,
            missingFactsOptions
          )
        : [],
    [
      selected,
      rule,
      element,
      ruleScopedOverrides,
      isEditMode,
      missingFactsOptions,
    ]
  );

  const editFields = useMemo(() => {
    if (!isEditMode || !rule || !selected?.provenance?.userSupplied) return [];
    const facts = selected.provenance.userSupplied.facts;
    return editFieldsForUserSupplied(
      rule,
      facts.map(f => f.factKey),
      facts
    );
  }, [isEditMode, rule, selected]);

  const activeFields = isEditMode ? editFields : promptFields;
  const stillNeededCount = isEditMode ? editFields.length : missing.length;

  const previewResult = useMemo(() => {
    if (!rule || !element || !selected || applyResult) return null;
    const draftOverrides = draftOverridesFromForm(
      activeFields,
      draft,
      parseDraftValue
    );
    return previewRuleResult(
      rule,
      element,
      ruleScopedOverrides,
      draftOverrides
    );
  }, [
    rule,
    element,
    selected,
    applyResult,
    activeFields,
    draft,
    ruleScopedOverrides,
  ]);

  const previewViolation = useMemo(() => {
    if (!selected || !rule || !previewResult) return null;
    return previewViolationFromResult(selected, previewResult, rule);
  }, [selected, rule, previewResult]);

  const previewBanner = useMemo(() => {
    if (!rule || !element || !previewResult) return null;
    const draftOverrides = draftOverridesFromForm(
      activeFields,
      draft,
      parseDraftValue
    );
    const merged = mergedFactsForPreview(
      element,
      ruleScopedOverrides,
      draftOverrides
    );
    return previewBannerForResult(previewResult, rule, merged.facts);
  }, [rule, element, previewResult, activeFields, draft, ruleScopedOverrides]);

  useEffect(() => {
    if (!selected?.id || !previewViolation || applyResult) {
      clearPreview();
      return;
    }
    const draftOverrides = draftOverridesFromForm(
      activeFields,
      draft,
      parseDraftValue
    );
    const merged = element
      ? mergedFactsForPreview(element, ruleScopedOverrides, draftOverrides)
      : null;
    const handle = window.setTimeout(() => {
      setPreview(selected.id, previewViolation, merged?.facts ?? null);
    }, 80);
    return () => window.clearTimeout(handle);
  }, [
    selected?.id,
    previewViolation,
    applyResult,
    setPreview,
    clearPreview,
    element,
    activeFields,
    draft,
    ruleScopedOverrides,
  ]);

  const allExceptionFieldCount = useMemo(() => {
    if (!selected || !rule || !element || isEditMode) return 0;
    const mergedMissing = missingFactsForViolation(
      selected,
      rule,
      element,
      ruleScopedOverrides,
      { allExceptionPaths: true }
    );
    const predicateKeys = new Set(
      missingFactsForViolation(selected, rule, element, ruleScopedOverrides, {
        allExceptionPaths: false,
      })
        .filter(m => !m.alreadySupplied)
        .map(m => m.factKey)
    );
    return mergedMissing.filter(m => !predicateKeys.has(m.factKey)).length;
  }, [selected, rule, element, ruleScopedOverrides, isEditMode]);

  const fieldKeySignature = useMemo(
    () => activeFields.map(f => f.factKey).join("|"),
    [activeFields]
  );

  const bulkCandidates = useMemo((): BulkApplyCandidate[] => {
    if (!applyResult || !rule || !element) return [];
    return findBulkApplyCandidates({
      rule,
      elements,
      sourceDbId: applyResult.sourceDbId,
      appliedFacts: applyResult.applied.map(rev => ({
        factKey: rev.factKey,
        label: rev.label,
        value: rev.value,
      })),
      allOverrides: overrides,
    });
  }, [applyResult, rule, element, elements, overrides]);

  useEffect(() => {
    setApplyResult(null);
    setJustSaved([]);
    setBulkDone(null);
    setRecheckNote(null);
    setDismissed(false);
    setMinimized(false);
    setShowAllExceptionPaths(false);
    clearPreview();
  }, [selected?.id, clearPreview]);

  useEffect(() => {
    if (!selected || !element) return;
    setDraft(
      buildFactFormDraft(activeFields, {
        audit:
          rule != null
            ? latestUserFactsForElementRule(
                project.id,
                element.dbId,
                rule.ruleId
              )
            : [],
        overrides: ruleScopedOverrides,
        provenanceFacts: selected.provenance?.userSupplied?.facts,
      })
    );
  }, [
    selected?.id,
    fieldKeySignature,
    ruleScopedOverrides,
    project.id,
    element?.dbId,
    rule?.ruleId,
  ]);

  useEffect(() => {
    if (!applyResult) {
      setBulkSelected(new Set());
      setBulkFactKeys(new Set());
      return;
    }
    setBulkSelected(new Set(bulkCandidates.map(c => c.dbId)));
    setBulkFactKeys(new Set(applyResult.applied.map(r => r.factKey)));
  }, [applyResult?.violationId, bulkCandidates.length]);

  const showInput =
    !dismissed &&
    selected &&
    rule &&
    element &&
    !applyResult &&
    ((selected.provenance?.status === "indeterminate" &&
      promptFields.length > 0) ||
      (isEditMode && editFields.length > 0));

  if (!showInput && !applyResult) return null;

  const onApply = () => {
    if (!rule || !element || !selected) return;
    const dbId = element.dbId;
    const applied: UserFactRevision[] = [];
    const priorOverrides = overrides[dbId] ?? {};

    for (const field of activeFields) {
      const raw = draft[field.factKey];
      const value = parseDraftValue(field, raw ?? "");
      if (value === undefined) continue;
      if (userFactValuesEqual(priorOverrides[field.factKey], value)) continue;
      const rev = setFact({
        projectId: project.id,
        dbId,
        factKey: field.factKey,
        label: field.label,
        ruleId: rule.ruleId,
        value,
      });
      applied.push(rev);
    }

    const mergedOverrides = { ...(ruleScopedOverrides ?? {}) };
    for (const field of activeFields) {
      const value = parseDraftValue(field, draft[field.factKey] ?? "");
      if (value !== undefined) mergedOverrides[field.factKey] = value;
    }

    const mergedEl = mergeElementWithUserFacts(
      element,
      { [dbId]: mergedOverrides },
      applied
    );
    const result = evaluateRule(rule, mergedEl);
    const remaining = missingFactsForEvaluation(
      rule,
      mergedEl.facts,
      selected,
      missingFactsOptions
    );

    if (applied.length === 0) {
      if (result?.status === "indeterminate" && remaining.length > 0) {
        setJustSaved([]);
        setRecheckNote(
          "No changes to save. Update a field below or answer the optional exception question."
        );
        return;
      }
    } else {
      setRecheckNote(null);
    }

    if (result?.status === "indeterminate" && remaining.length > 0) {
      setJustSaved(applied);
      const nextFields = promptFieldsForViolation(
        {
          ...selected,
          provenance: { ...selected.provenance!, status: "indeterminate" },
        },
        rule,
        element,
        mergedOverrides,
        missingFactsOptions
      );
      setDraft(
        buildFactFormDraft(nextFields, {
          audit: latestUserFactsForElementRule(project.id, dbId, rule.ruleId),
          overrides: mergedOverrides,
        })
      );
      return;
    }

    setJustSaved([]);
    clearPreview();
    setApplyResult({
      violationId: selected.id,
      sourceDbId: dbId,
      status: result?.status ?? "compliant",
      summary: result?.summary ?? "Element passes this rule.",
      rationale: result?.rationale,
      applied,
      observed: result?.observedValue,
      threshold: result?.thresholdApplied,
    });
  };

  const onBulkApply = () => {
    if (
      !rule ||
      !applyResult ||
      bulkSelected.size === 0 ||
      bulkFactKeys.size === 0
    ) {
      return;
    }
    const sourceRevisions = new Map(
      applyResult.applied.map(r => [r.factKey, r])
    );
    let count = 0;
    for (const candidate of bulkCandidates) {
      if (!bulkSelected.has(candidate.dbId)) continue;
      for (const factKey of candidate.factKeys) {
        if (!bulkFactKeys.has(factKey)) continue;
        const source = sourceRevisions.get(factKey);
        if (!source) continue;
        setFact({
          projectId: project.id,
          dbId: candidate.dbId,
          factKey,
          label: source.label,
          ruleId: rule.ruleId,
          value: source.value,
          copiedFromDbId: applyResult.sourceDbId,
          copiedFromVersion: source.version,
        });
      }
      count += 1;
    }
    setBulkDone(count);
  };

  const onDismiss = () => {
    clearPreview();
    setApplyResult(null);
    setDraft({});
    setJustSaved([]);
    setBulkDone(null);
  };

  if (applyResult) {
    const v = verdictDisplay(applyResult.status, applyResult.rationale);
    return (
      <PromptShell borderColor={v.color} title="Result" onClose={onDismiss}>
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: v.color,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Result — saved to audit log
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: v.color,
            marginBottom: 6,
          }}
        >
          {v.title}
        </div>
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            marginBottom: 8,
          }}
        >
          {applyResult.summary}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--fc2-text-dim)",
            marginBottom: 10,
            lineHeight: 1.45,
          }}
        >
          {v.detail}
        </div>

        <div
          style={{
            fontSize: 11,
            background: "rgba(255,255,255,0.04)",
            borderRadius: 6,
            padding: "8px 10px",
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Saved values</div>
          {applyResult.applied.map(rev => (
            <div key={rev.factKey} style={{ marginBottom: 4 }}>
              {rev.label ?? rev.factKey}: {formatValue(rev.value)} · v
              {rev.version} · {new Date(rev.updatedAt).toLocaleString()}
            </div>
          ))}
        </div>

        {applyResult.observed && applyResult.threshold && (
          <div
            style={{
              fontSize: 11,
              color: "var(--fc2-text-dim)",
              marginBottom: 10,
            }}
          >
            Measured {applyResult.observed.value} {applyResult.observed.unit} vs
            required {applyResult.threshold.value} {applyResult.threshold.unit}
          </div>
        )}

        {bulkCandidates.length > 0 && element && (
          <BulkApplyPanel
            candidates={bulkCandidates}
            applied={applyResult.applied}
            sourceLabel={elementFamilyLabel(element)}
            selected={bulkSelected}
            factKeys={bulkFactKeys}
            bulkDone={bulkDone}
            onToggleCandidate={dbId => {
              setBulkSelected(prev => {
                const next = new Set(prev);
                if (next.has(dbId)) next.delete(dbId);
                else next.add(dbId);
                return next;
              });
            }}
            onToggleFactKey={key => {
              setBulkFactKeys(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              });
            }}
            onSelectAll={() =>
              setBulkSelected(new Set(bulkCandidates.map(c => c.dbId)))
            }
            onClearAll={() => setBulkSelected(new Set())}
            onApply={onBulkApply}
          />
        )}

        <button
          type="button"
          onClick={onDismiss}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 6,
            border: "none",
            background: v.color,
            color: "#1a1a1a",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          OK — update violations list
        </button>
      </PromptShell>
    );
  }

  const shellTitle =
    previewBanner?.title ??
    (isEditMode ? "Update verified values" : "Supply missing facts");

  return (
    <PromptShell
      borderColor={
        previewBanner?.tone === "fail"
          ? "#e87070"
          : previewBanner?.tone === "pass"
            ? "#22cc66"
            : isEditMode
              ? "#e87070"
              : "#e5b85f"
      }
      title={shellTitle}
      minimized={minimized}
      onToggleMinimize={() => setMinimized(m => !m)}
      onClose={() => {
        clearPreview();
        setDismissed(true);
      }}
    >
      {!minimized && (
        <>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color:
                previewBanner?.tone === "fail"
                  ? "#e87070"
                  : previewBanner?.tone === "pass"
                    ? "#22cc66"
                    : isEditMode
                      ? "#e87070"
                      : "#e5b85f",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            {previewBanner?.title ??
              (isEditMode
                ? "Verified by you — update values"
                : `Needs info — ${stillNeededCount} field${stillNeededCount === 1 ? "" : "s"} still required`)}
          </div>

          {previewBanner && (
            <div
              style={{
                fontSize: 12,
                background:
                  previewBanner.tone === "fail"
                    ? "rgba(232,112,112,0.12)"
                    : previewBanner.tone === "pass"
                      ? "rgba(34,204,102,0.12)"
                      : "rgba(229,184,95,0.12)",
                border: `1px solid ${
                  previewBanner.tone === "fail"
                    ? "rgba(232,112,112,0.4)"
                    : previewBanner.tone === "pass"
                      ? "rgba(34,204,102,0.35)"
                      : "rgba(229,184,95,0.4)"
                }`,
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: 10,
                lineHeight: 1.45,
                color: "var(--fc2-text)",
              }}
            >
              {previewBanner.body}
              {!showAllExceptionPaths &&
                previewBanner.tone === "review" &&
                allExceptionFieldCount > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      onClick={() => setShowAllExceptionPaths(true)}
                      style={{
                        padding: 0,
                        border: "none",
                        background: "none",
                        color: "var(--fc2-text-dim)",
                        fontSize: 11,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textDecoration: "underline",
                      }}
                    >
                      Show all exception paths ({allExceptionFieldCount}{" "}
                      optional fields)
                    </button>
                  </div>
                )}
            </div>
          )}

          {recheckNote && (
            <div
              style={{
                fontSize: 12,
                color: "var(--fc2-text-dim)",
                marginBottom: 10,
                lineHeight: 1.45,
              }}
            >
              {recheckNote}
            </div>
          )}

          {justSaved.length > 0 && !isEditMode && (
            <div
              style={{
                fontSize: 12,
                background: "rgba(34,204,102,0.12)",
                border: "1px solid rgba(34,204,102,0.35)",
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: 10,
                lineHeight: 1.45,
              }}
            >
              <strong>Saved</strong>{" "}
              {justSaved
                .map(r => `${r.label ?? r.factKey}: ${formatValue(r.value)}`)
                .join(" · ")}
              {missing.length > 0
                ? `. ${missing.length} field${missing.length === 1 ? "" : "s"} still optional below.`
                : "."}
            </div>
          )}

          {isEditMode &&
            selected &&
            (selected.provenance?.userSupplied?.facts.length ?? 0) > 0 && (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--fc2-text-dim)",
                  marginBottom: 10,
                  lineHeight: 1.45,
                }}
              >
                Pre-filled from your last entry (v
                {Math.max(
                  ...selected.provenance!.userSupplied!.facts.map(
                    f => f.version
                  )
                )}
                ). Change a value and re-check to create a new version.
              </div>
            )}

          <div
            style={{
              fontSize: 12,
              color: "var(--fc2-text-dim)",
              marginBottom: 12,
              lineHeight: 1.45,
            }}
          >
            {isEditMode
              ? "Values you supplied are shown below. Editing creates a new audit version."
              : "FC2 could not read this from the model. Each answer is versioned and the rule re-checks as you go."}
          </div>

          {activeFields.map(field => (
            <MissingFactFieldInput
              key={field.factKey}
              field={field}
              value={draft[field.factKey] ?? ""}
              onInput={v => {
                setRecheckNote(null);
                setDraft(d => ({ ...d, [field.factKey]: v }));
              }}
            />
          ))}

          {stillNeededCount === 0 && !isEditMode && activeFields.length > 0 && (
            <div
              style={{
                fontSize: 12,
                color: "var(--fc2-text-dim)",
                marginBottom: 8,
              }}
            >
              All required fields are filled. Update a saved value or click
              Apply to re-check.
            </div>
          )}

          <button
            type="button"
            onClick={onApply}
            style={{
              width: "100%",
              marginTop: 4,
              padding: "8px 12px",
              borderRadius: 6,
              border: "none",
              background: isEditMode ? "#e87070" : "#e5b85f",
              color: "#1a1a1a",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {isEditMode ? "Update & re-check" : "Apply & re-check"}
          </button>
        </>
      )}
    </PromptShell>
  );
}

function PromptShell({
  borderColor,
  title,
  children,
  onClose,
  minimized = false,
  onToggleMinimize,
}: {
  borderColor: string;
  title?: string;
  children: ComponentChildren;
  onClose?: () => void;
  minimized?: boolean;
  onToggleMinimize?: () => void;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
  } | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const onDragPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onDragPointerMove = (e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    setOffset({
      x: drag.baseX + (e.clientX - drag.startX),
      y: drag.baseY + (e.clientY - drag.startY),
    });
  };

  const onDragPointerUp = (e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const headerRightPad = (onClose ? 36 : 0) + (onToggleMinimize ? 36 : 0);

  return (
    <div
      ref={shellRef}
      role="dialog"
      aria-label="Supply missing information"
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        width: "min(360px, calc(100% - 24px))",
        maxHeight: minimized ? "none" : "min(480px, 58vh)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "rgba(18,18,22,0.98)",
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        zIndex: 20,
        pointerEvents: "auto",
      }}
    >
      <div
        role="toolbar"
        aria-label="Drag to move panel"
        onPointerDown={onDragPointerDown}
        onPointerMove={onDragPointerMove}
        onPointerUp={onDragPointerUp}
        onPointerCancel={onDragPointerUp}
        style={{
          flexShrink: 0,
          position: "relative",
          height: 36,
          display: "flex",
          alignItems: "center",
          padding: `0 ${12 + headerRightPad}px 0 12px`,
          cursor: "grab",
          userSelect: "none",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--fc2-text)",
          borderBottom: minimized ? "none" : "1px solid var(--fc2-border)",
          background: "rgba(255,255,255,0.03)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: "var(--fc2-text-dim)",
            marginRight: 8,
            flexShrink: 0,
          }}
        >
          Drag
        </span>
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          }}
        >
          {title ?? "Missing facts"}
        </span>
      </div>
      {onToggleMinimize && (
        <button
          type="button"
          aria-label={minimized ? "Expand panel" : "Minimize panel"}
          onClick={onToggleMinimize}
          style={shellIconButtonStyle(onClose ? 40 : 8)}
        >
          {minimized ? "▢" : "−"}
        </button>
      )}
      {onClose && (
        <button
          type="button"
          aria-label="Hide panel"
          onClick={onClose}
          style={shellIconButtonStyle(8)}
        >
          ×
        </button>
      )}
      {!minimized && (
        <div
          style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            padding: "14px 16px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function shellIconButtonStyle(right: number) {
  return {
    position: "absolute" as const,
    top: 4,
    right,
    width: 28,
    height: 28,
    border: "none",
    borderRadius: 4,
    background: "rgba(255,255,255,0.08)",
    color: "var(--fc2-text-dim)",
    cursor: "pointer",
    fontSize: 16,
    lineHeight: 1,
    fontFamily: "inherit",
    zIndex: 1,
  };
}

function formatValue(value: UserFactRevision["value"]): string {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "unit" in value
  ) {
    return `${value.value} ${value.unit}`;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function BulkApplyPanel({
  candidates,
  applied,
  sourceLabel,
  selected,
  factKeys,
  bulkDone,
  onToggleCandidate,
  onToggleFactKey,
  onSelectAll,
  onClearAll,
  onApply,
}: {
  candidates: BulkApplyCandidate[];
  applied: UserFactRevision[];
  sourceLabel: string;
  selected: Set<number>;
  factKeys: Set<string>;
  bulkDone: number | null;
  onToggleCandidate: (dbId: number) => void;
  onToggleFactKey: (key: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onApply: () => void;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid var(--fc2-border)",
        borderRadius: 6,
        padding: "8px 10px",
        marginBottom: 10,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Apply to similar elements?
      </div>
      <div
        style={{
          color: "var(--fc2-text-dim)",
          lineHeight: 1.45,
          marginBottom: 8,
        }}
      >
        {candidates.length} other{" "}
        {candidates.length === 1 ? "element" : "elements"} match type "
        {sourceLabel}", the same rule, and are missing the same facts. Nothing
        with model data or an existing override is included.
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Values to copy</div>
        {applied.map(rev => (
          <label
            key={rev.factKey}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={factKeys.has(rev.factKey)}
              onChange={() => onToggleFactKey(rev.factKey)}
            />
            {rev.label ?? rev.factKey}: {formatValue(rev.value)}
          </label>
        ))}
      </div>

      <details style={{ marginBottom: 8 }}>
        <summary style={{ cursor: "pointer", userSelect: "none" }}>
          Preview ({selected.size} of {candidates.length} selected)
        </summary>
        <ul
          style={{
            margin: "6px 0 0",
            paddingLeft: 18,
            maxHeight: 120,
            overflowY: "auto",
          }}
        >
          {candidates.map(c => (
            <li key={c.dbId} style={{ marginBottom: 4 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.dbId)}
                  onChange={() => onToggleCandidate(c.dbId)}
                />
                {c.displayName}
              </label>
            </li>
          ))}
        </ul>
      </details>

      {!bulkDone && (
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <button
            type="button"
            onClick={onSelectAll}
            style={bulkLinkButtonStyle}
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onClearAll}
            style={bulkLinkButtonStyle}
          >
            Clear
          </button>
        </div>
      )}

      {bulkDone != null ? (
        <div
          style={{
            color: "#22cc66",
            marginBottom: 4,
            lineHeight: 1.45,
          }}
        >
          Applied to {bulkDone} element{bulkDone === 1 ? "" : "s"}. Each copy is
          versioned in the audit log (copied from this element).
        </div>
      ) : (
        <button
          type="button"
          disabled={selected.size === 0 || factKeys.size === 0}
          onClick={onApply}
          style={{
            width: "100%",
            padding: "6px 10px",
            borderRadius: 4,
            border: "1px solid var(--fc2-border)",
            background:
              selected.size > 0 && factKeys.size > 0
                ? "rgba(255,255,255,0.08)"
                : "transparent",
            color: "var(--fc2-text)",
            fontSize: 12,
            fontWeight: 600,
            cursor:
              selected.size > 0 && factKeys.size > 0
                ? "pointer"
                : "not-allowed",
            fontFamily: "inherit",
          }}
        >
          Apply to {selected.size} selected element
          {selected.size === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

const bulkLinkButtonStyle = {
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--fc2-text-dim)",
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "underline",
} as const;

function MissingFactFieldInput({
  field,
  value,
  onInput,
}: {
  field: MissingFactField;
  value: string;
  onInput: (v: string) => void;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the select (enum) and input (quantity) controls are rendered conditionally on field.kind, which the rule cannot see statically
    <label
      style={{
        display: "block",
        marginBottom: 10,
        fontSize: 12,
      }}
    >
      <span style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
        {field.label}
        {field.alreadySupplied && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              fontWeight: 600,
              color: "#22cc66",
            }}
          >
            SAVED
          </span>
        )}
      </span>
      {field.context && (
        <span
          style={{
            display: "block",
            marginBottom: 6,
            fontSize: 11,
            color: "var(--fc2-text-dim)",
            lineHeight: 1.35,
          }}
        >
          {field.context}
        </span>
      )}

      {field.kind === "boolean" && (
        <div style={{ display: "flex", gap: 8 }}>
          {(["true", "false"] as const).map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => onInput(opt)}
              style={{
                flex: 1,
                padding: "6px 8px",
                borderRadius: 4,
                border:
                  value === opt
                    ? "1px solid #e5b85f"
                    : "1px solid var(--fc2-border)",
                background:
                  value === opt ? "rgba(229,184,95,0.2)" : "var(--fc2-bg)",
                color: "var(--fc2-text)",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {opt === "true" ? "Yes" : "No"}
            </button>
          ))}
        </div>
      )}

      {field.kind === "enum" && field.enumOptions && (
        <select
          value={value}
          onChange={e => onInput(e.currentTarget.value)}
          style={{
            width: "100%",
            padding: "6px 8px",
            borderRadius: 4,
            border: "1px solid var(--fc2-border)",
            background: "var(--fc2-bg)",
            color: "var(--fc2-text)",
            fontSize: 13,
            fontFamily: "inherit",
          }}
        >
          <option value="">Select…</option>
          {field.enumOptions.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {field.kind === "quantity" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            step="any"
            min={0}
            value={value}
            onInput={e => onInput(e.currentTarget.value)}
            placeholder={`Value (${unitHintForField(field)})`}
            style={{
              flex: 1,
              padding: "6px 8px",
              borderRadius: 4,
              border: "1px solid var(--fc2-border)",
              background: "var(--fc2-bg)",
              color: "var(--fc2-text)",
              fontSize: 13,
              fontFamily: "inherit",
            }}
          />
          <span
            style={{
              fontSize: 11,
              color: "var(--fc2-text-dim)",
              minWidth: 72,
            }}
          >
            {unitHintForField(field)}
          </span>
        </div>
      )}
    </label>
  );
}

function unitHintForField(field: MissingFactField): string {
  if (field.factKey.includes("Ratio") || field.factKey.includes("ratio")) {
    return "% of floor area";
  }
  if (field.factKey.toLowerCase().includes("lux")) return "lux";
  if (field.factKey.toLowerCase().includes("ach")) return "ACH";
  return UNIT_HINT[field.unit];
}
